import { useEffect } from 'react';
import { observer } from 'mobx-react';
import { DealEconomicsDialog } from './dealEconomics/dealEconomicsDialog';
import { RequestedClosingDateDialog } from './requestedClosingDate/requestedClosingDateDialog';
import { ExpectedClosingDateDialog } from './expectedClosingDate/expectedClosingDateDialog';
import { TrackingNumberDialog } from './trackingNumber/trackingNumberDialog';
import { ACHFormDialog } from './achForm/achForm';
import { LoanPaymentsDialog } from './loanPayments/loanPayments/loanPaymentsDialog';
import LoanFastTrackPaymentDialog from './loanFastTrackPayment/loanFastTrackPayment/loanFastTrackPaymentDialog';
import { InsuranceProviderDialog } from './insuranceProvider/insuranceProviderDialog';
import { InsuranceApprovalDialog } from './insuranceApproval/insuranceApprovalDialog';
import { InsuranceDocumentDialog } from './insuranceDocument/insuranceDocumentDialog';
import { ExtensionDetailsDialog } from './extensionDetails/extensionDetailsDialog';
import { FundingTemplateDialog } from './fundingTemplate/fundingTemplateDialog';
import BorrowerSurveyDialog from './borrowerSurvey/borrowerSurveyDialog';
import BorrowerEntityDialog from './borrowerEntity/borrowerEntityDialog';
import LenderFeesDialog from './lenderFees/lenderFeesDialog';
import { TitleAgentV2Dialog } from './titleAgentV2/titleAgentV2Dialog';
import FundingPreferenceDialog from './fundingPreference/fundingPreferenceDialog';
import { ExpectedWireReleaseDateDialog } from './expectedWireReleaseDate/expectedWireReleaseDateDialog';
import BorrowerClosingAgentDialog from './borrowerClosingAgent/borrowerClosingAgentDialog';
import {
  ScopeOfWorkV2FormDialog,
  ScopeOfWorkV2LinkDialog,
} from '@roc/feature-sow-v2';
import TermSheetDialog from './termSheet/termSheet/termSheetDialog';
import NoteAllongeDialog from './noteAllonge/noteAllongeDialog';
import AssignmentAndConveyanceDialog from './assignmentAndConveyance/assignmentAndConveyanceDialog';
import { PointOfContactDialog, DrawReportDialog, DrawReviewReportDialog, DrawInformationDialog, WireAuthorizationDialog, InspectionInvoiceDetailsDialog, RevisedScopeOfWorkFormDialog, TakePropertyPhotosDialog } from '@roc/feature-draws';

import {
  DEAL_ECONOMICS,
  REQUESTED_CLOSING_DATE,
  EXPECTED_CLOSING_DATE,
  DUE_DILIGENCE_PAYMENT,
  TRACKING_NUMBER,
  LOAN_FAST_TRACK_PAYMENT,
  TERM_SHEET,
  BORROWER_SURVEY_QUESTIONNAIRE,
  BORROWER_ENTITY,
  LENDER_FEES,
  TITLE_AGENT,
  INSURANCE_PROVIDER,
  FUNDING_PREFERENCE,
  EXPECTED_WIRE_RELEASE_DATE,
  INSURANCE_APPROVAL,
  LOAN_FUNDING_TEMPLATE,
  APPRAISAL_ORDER_DETAILS,
  BORROWER_CLOSING_AGENT,
  NOTE_ALLONGE,
  ASSIGNMENT_AND_CONVEYANCE,
  SCOPE_OF_WORK_FORM_V2,
  SCOPE_OF_WORK_LINK_V2,
  POINT_OF_CONTACT,
  RATE_LOCK_EXTENSION,
  TITLE_COMPANY_BANK_INFORMATION,
  RESOLUTION_ITEM_FORM,
  DRAW_REPORT,
  BORROWER_BACKGROUND_REVIEW,
  BORROWER_CREDIT_REVIEW,
  TRACK_RECORD_REVIEW,
  CREDIT_REPORT,
  TRIMERGE_CREDIT_REPORT,
  CLOSING_CONDITION,
  APPRAISAL_DETAILS,
  ACH_FORM,
  APPRAISAL_REPORT_ARV,
  APPRAISAL_REPORT_AS_IS,
  APPRAISAL_REPORT_AS_IS_RENT,
  APPRAISAL_REPORT,
  BORROWER_SETUP,
  TRACK_RECORD,
  APPRAISAL_REVIEW_REPORT,
  REFERRAL_INFORMATION,
  BANK_STATEMENT_PREFIX,
  LEASE_INFORMATION_UNIT_PREFIX,
  TAX_ESCROW,
  POST_DISASTER_INSPECTION,
  PERSONAL_IDENTIFICATION,
  CONSTRUCTION_FEASIBILITY_REVIEW,
  INTERNAL_VALUATION_REPORT,
  BANK_STATEMENT_MONTH_PREFIX,
  PERMITS,
  INSURANCE_INFORMATION,
  INSURANCE_INFORMATION_REVIEW,
  CREDIT_AUTHORIZATION,
  BORROWER_IDENTITY_VERIFICATION,
  AIDocType,
  CLOSING_REVIEW,
  DRAW_REQUEST_FORM,
  DRAW_INFORMATION,
  WIRE_AUTHORIZATION,
  INSPECTION_INVOICE_DETAILS,
  REVISED_SOW_FORM,
  TAKE_PROPERTY_PHOTOS,
  SITE_INSPECTION_CM_REPORT,
  TAKE_INTERNAL_VALUATION_PHOTOS,
  INTERNAL_VALUATION,
  PAYOFF_REQUEST_DETAILS,
  SERVICER_PROVIDED_DATA,
  SERVICER_FEES,
  INTEREST_ALLOCATION,
  FORECLOSURE_INTEREST_ALLOCATION,
  PAYOFF_CORRECTION,
  PAYABLE_ATTESTATION,
  DQ_TAXES_ATTESTATION,
  PAYOFF_CALCULATOR,
  TRACK_RECORD_RESOLUTION_ITEM_FORM,
  CONFIRM_FIRST_APPRAISAL_VALUE,
  BACKGROUND_REPORT,
  CERTIFICATE_OF_GOOD_STANDING,
  MANUAL_TASK,
  TITLE_DOCUMENTS_REVIEW,
  POST_CLOSING_REVIEW,
  PAYOFF_RECONCILIATION_REVIEW,
  UNDERWRITE_THE_LOAN,
  ACCEPT_LOAN_TERMS,
  CONFIRM_LOAN_TERMS,
  VALUATION_DETAILS,
  INSURANCE_DOCUMENT,
  EXTENSION_DETAILS,
  EXTENSION_AGREEMENT,
  TITLE_AGENCY,
  PROPERTY_APPRAISALS,
  PROPERTY_VALUATIONS,
  POST_MORTEM,
  DELINQUENCY_STATUS,
  FORECLOSURE_ATTORNEY,
  FORECLOSURE_STAGES,
  REALIZED_LOSS,
  SETTLEMENT_DETAILS,
  VALUATION_PAYMENT,
  LEASE_AGREEMENT_PREFIX,
  BANK_STATEMENT_TASK_CODE,
  PRESCREEN,
  CLIENT_VALUATION_DISPUTE,
  INTERNAL_VALUATION_ROV_FOR_APPRAISAL,
  INSURANCE_DETAILS,
  RESOLUTION_FORM,
  DOC_REVIEW_FORM,
  TITLE_COMMITMENT,
  LIEN_RESOLUTION_DOCUMENT,
  BORROWER_LIQUIDITY,
  LEASE_AGREEMENT,
  APPRAISAL_REASSIGNMENT_LETTER,
  ORIGINATOR_STATE_LICENSE,
  COMPANY_STATE_LICENSE,
  PURCHASE_CONTRACT
} from '../../constants';
import { ResolutionModal } from '@roc/feature-resolution';
import { DocReviewDialogV2, PurchaseContractReviewDialog } from '@roc/feature-ai-docs-review';
import { RateLockExtensionDialog } from './../documentForms/rateLockExtension/rateLockExtensionDialog';
import { ForeclosureStageListItem, useLoanStore } from '@roc/feature-loans';
import { useDocumentStore } from '../../hooks/useDocumentStore';
import { TitleCompanyBankInformationDialog } from './titleCompanyBankInformation/titleCompanyBankInformationDialog';
import { PlaidCopyUrlModal } from '@roc/feature-plaid';
import { DocumentStore } from '../../stores/documentStore';
import { useUserStore } from '@roc/feature-app-core';
import ResolutionItem from './resolutionItem/resolutionItem';
import { BorrowerBackgroundReviewDialog } from './borrowerBackgroundReview/borrowerBackgroundReviewDialog';
import { BorrowerCreditReviewDialog } from './borrowerCreditReview/borrowerCreditReviewDialog'
import { CreditReport } from './creditReport/creditReport';
import { LoanClosingConditionModal } from './loanClosingCondition/loanClosingConditionDialog';
import { AppraisalOrderDetailsWithARRDialog } from './appraisalOrderAndCDADetails/loanPayments/appraisalOrderDetailsDialog';
import { AppraisalOrderDetailsDialog } from './appraisalOrderDetails/appraisalOrderDetailsDialog';
import { useStore } from '@roc/client-portal-shared/hooks';
import { BorrowerSetupDialog } from './borrowerSetup/borrowerSetupDialog';
import EstimateFundingTemplateDialog from './fundingTemplate/estimateFundingTemplateNumbers';
import { TrackRecordLineItemDialog } from './borrowerBaseballCard/trackRecordLineItemDialog';
import { AppraisalReviewReportDialog } from './appraisalReviewReport/appraisalReviewReportDialog';
import { ReferrerInformationDialog } from './referralFee/referrerInformationDialog';
import BankStatementDialog from './bankStatement/bankStatementDialog';
import LeaseInformationDialog from './leaseInformation/leaseInformationDialog';
import TaxEscrowDialog from './taxEscrow/taxEscrowDialog';
import { isOpenManualQuote, LoanProcess, LoanStatus, LoanType, ManualQuoteStatus, SupportRoles } from '@roc/feature-utils';
import { BorrowerSetupToDoDialog, IdAndFinancialInformationDialog, PersonalAndCreditInformationDialog } from '@roc/feature-borrowers';
import PostDisasterInspectionDialog from './postDisasterInspection/postDisasterInspectionDialog';
import { PersonalIdentificationDialog } from './personalIdentification/personalIdentificationDialog';
import { ConstructionFeasibilityToDoDialog } from 'libs/feature-construction-feasibility/src';
import { InternalValuationReportDialog } from './internalValuationReport/internalValuationReportDialog';
import { PermitsDialog } from './permits/permitsDialog';
import { AcceptLoanTermsDialog } from './acceptLoanTerms/acceptLoanTermsDialog';
import { InsuranceInformationAndReviewDialog, InsuranceInformationReviewDialog } from '@roc/feature-insurance-review';
import { AttestationDialog, ForeclosureInterestAllocationModal, InterestAllocationModal, PayoffCalculatorDialog, PayoffCorrectionDialog, PayoffRequestDetailDialog, ServicerFees, ServicerProvidedDataDialog, PayoffReconciliationReviewDialog } from '@roc/feature-payoffs';
import { DocReviewDialog } from 'libs/feature-ai-docs-review/src';
import { InternalValuationPaymentLinkForm } from './internalValuation/internalValuationPaymentLinkForm';
import TrackRecordResolutionItem from './resolutionItem/trackRecordResolutionItem';
import { FirstAppraisalValueConfirmationDialog } from './firstAppraisalValueConfirmation/firstAppraisalValueConfirmationDialog';
import CertificateOfGoodStandingDialog from './entity/certificateOfGoodStandingDialog';
import ManualTasksItem from './manualTasks/manualTasksItem';
import { RealValPropertyPhotosDialog, SettlementDetailsItem } from '@roc/feature-documents';
import UnderwriteTheLoanDialog from './underwriteTheLoan/underwriteTheLoanDialog';
import { PricingTermLoanDialog } from './pricingTermLoan/pricingTermLoanDialog';
import { ManualQuoteLoanDialog } from './manualQuoteLoan/manualQuoteLoanDialog';
import { PointOfContactDialog as GenericPointOfContact } from './PointOfContact/PointOfContactDialog';
import { AppraisalDetailsDialogV2 } from './appraisalDetailsV2/components/appraisalDetailsDialogV2';
import { TitleAgencyInfoItem } from 'libs/feature-loan-details/src/loanDetails/delinquency/titleAgent';
import { RealizedLossItem } from 'libs/feature-loan-details/src/loanDetails/delinquency/realizedLoss';
import { DEFAULT_DELINQUENCY_STATUS_VALUES, DelinquencyStatusItem } from 'libs/feature-loan-details/src/loanDetails/delinquency/delinquencyStatus';
import { PostMortemItem } from 'libs/feature-loan-details/src/loanDetails/delinquency/postMortem';
import { PropertyAppraisalsItem } from 'libs/feature-loan-details/src/loanDetails/delinquency/propertyAppraisals';
import { ForeclosureAttorneyItem } from 'libs/feature-loan-details/src/loanDetails/delinquency/foreclosureAttorney';
import { AppraisalPaymentLinkModal } from './appraisalDetailsV2/components/appraisalDetails';
import { ExtensionAgreementDialog } from './extensionAgreement/extensionAgreementDialog';
import { DelinquencyBpoDashboardsItem } from './delinquencyBPO/DelinquencyBpoDashboardsItem';
import { PrescreenDialog } from './prescreen/prescreenDialog';
import { ValuationDisputeDialog } from './valuationDispute/valuationDisputeDialog';
import { InsuranceDetailsItem } from '@roc/feature-insurance-details';
import { TitleCommitmentDialog, LienResolutionContent } from '@roc/feature-lien';
import { BorrowerLiquidityDialog } from '@roc/feature-bank-statements';
import { LeaseModal, LeaseUnitModal } from '@roc/feature-leases';
import { AppraisalReassignmentLetterDialog } from '@roc/feature-appraisal-reassignment-letter';
import { StateLicenseTaskDialog } from './stateLicense/stateLicenseTaskDialog';

export type DocumentFormsProps = {
  loanId: string;
  onClose?: () => void;
};

export const DocumentForms = observer((props: DocumentFormsProps) => {
  const { loanId, onClose } = props;
  const { loanStore } = useLoanStore();
  const {
    documentStore: { documentFormStore, currentDocument },
  } = useDocumentStore();

  const { globalStore, insuranceInformationStore,
    insuranceInformationReviewStore, appraisalDetailsV2Store } = useStore();

  const { documentStore } = useDocumentStore();
  const closeCurrentForm = () => {
    onClose && onClose();
    documentFormStore.closeCurrentForm();
  };

  const closeNoteAllongeAndAssignment = () => {
    onClose && onClose();
    documentFormStore.onCloseNoteAllongeAndAssignment();
  }

  const titleAgencyShowAlert = (message: string, _type: 'success' | 'danger') => {
    alert(message);
  };

  const plaidShareBaseURL = window.location.href.split('lender-portal');
  const { userStore } = useUserStore();
  const allowLoanPricing = userStore.allowLoanPricing;
  const currentForm = documentFormStore.currentForm;
  const isClosingConditionForm = currentForm && currentForm.includes('Closing Condition - ');

  useEffect(() => {
    return () => {
      documentFormStore.reset();
    };
  }, [documentFormStore]);

  const getCorrectAcceptLoanTermDialog = () => {
    if (isOpenManualQuote(loanStore?.loanDetails?.manualQuoteLoanDetailValues?.manualQuoteStatus)) {
      return (
        <ManualQuoteLoanDialog
          open={documentFormStore.currentForm === ACCEPT_LOAN_TERMS}
          onClose={closeCurrentForm}
          document={currentDocument}
        />
      );
    }

    if (loanStore?.loanDetails?.loanType === LoanType.RESIDENTIAL_BRIDGE) {
      return (
        <AcceptLoanTermsDialog
          open={documentFormStore.currentForm === ACCEPT_LOAN_TERMS}
          onClose={closeCurrentForm}
          loan={loanStore.loanDetails}
          document={currentDocument}
        />
      )
    } else {
      return (
        <PricingTermLoanDialog
          open={documentFormStore.currentForm === ACCEPT_LOAN_TERMS}
          onClose={closeCurrentForm}
          document={currentDocument}
        />
      );
    }
  };

  return (
    <>
      {allowLoanPricing &&
        <DealEconomicsDialog
          open={documentFormStore.currentForm === DEAL_ECONOMICS}
          onClose={closeCurrentForm}
          document={currentDocument}
        />
      }
      <ExpectedClosingDateDialog
        open={documentFormStore.currentForm === EXPECTED_CLOSING_DATE}
        onClose={closeCurrentForm}
        loanId={loanId}
        document={currentDocument}
        store={documentFormStore.expectedClosingDateStore}
      />
      <TrackingNumberDialog
        open={documentFormStore.currentForm === TRACKING_NUMBER}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      {globalStore.userFeatures?.allowACHForm && <ACHFormDialog
        open={documentFormStore.currentForm === ACH_FORM}
        onClose={closeCurrentForm}
        loan={loanStore.loanDetails}
        document={currentDocument}
      />}
      {/* not a template anymore */}
      <RequestedClosingDateDialog
        open={documentFormStore.currentForm === REQUESTED_CLOSING_DATE}
        onClose={closeCurrentForm}
        loanId={loanId}
        store={documentFormStore.requestedClosingDateStore}
      />
      <ReferrerInformationDialog
        open={documentFormStore.currentForm === REFERRAL_INFORMATION}
        onClose={closeCurrentForm}
        loanId={loanId}
        brokerId={loanStore?.loanDetails?.broker?.lenderId}
        document={currentDocument}
      />
      <LoanPaymentsDialog
        open={documentFormStore.currentForm === DUE_DILIGENCE_PAYMENT}
        onClose={closeCurrentForm}
        loan={loanStore.loanDetails}
        document={currentDocument}
      />
      <LoanFastTrackPaymentDialog
        open={documentFormStore.currentForm === LOAN_FAST_TRACK_PAYMENT}
        onClose={closeCurrentForm}
        loan={loanStore.loanDetails}
        document={currentDocument}
      />
      <TermSheetDialog
        open={documentFormStore.currentForm === TERM_SHEET}
        onClose={closeCurrentForm}
        loan={loanStore.loanDetails}
        document={currentDocument}
      />
      {/* not a template anymore - only for Multifamily which is no longer available*/}
      <BorrowerSurveyDialog
        open={documentFormStore.currentForm === BORROWER_SURVEY_QUESTIONNAIRE}
        onClose={closeCurrentForm}
        loanId={loanId}
        document={currentDocument}
      />
      <BorrowerEntityDialog
        open={documentFormStore.currentForm === BORROWER_ENTITY}
        onClose={closeCurrentForm}
        loanId={loanId}
        document={currentDocument}
      />
      {allowLoanPricing &&
        <LenderFeesDialog
          open={documentFormStore.currentForm === LENDER_FEES}
          onClose={closeCurrentForm}
          loanId={loanId}
          document={currentDocument}
        />
      }
      <TitleAgentV2Dialog
        open={documentFormStore.currentForm === TITLE_AGENT}
        onClose={closeCurrentForm}
        loanId={loanId}
        document={currentDocument}
      />
      <InsuranceProviderDialog
        open={documentFormStore.currentForm === INSURANCE_PROVIDER}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      <FundingPreferenceDialog
        open={documentFormStore.currentForm === FUNDING_PREFERENCE}
        onClose={closeCurrentForm}
        loanId={loanId}
        document={currentDocument}
      />
      <ExpectedWireReleaseDateDialog
        open={documentFormStore.currentForm === EXPECTED_WIRE_RELEASE_DATE}
        onClose={closeCurrentForm}
        loanId={loanId}
        store={documentFormStore.expectedWireReleaseDateStore}
        document={currentDocument}
      />
      <DocReviewDialog
        open={documentFormStore.currentForm === CLOSING_REVIEW}
        onClose={closeCurrentForm}
        type={AIDocType.CLOSING_DOCS}
        loanId={loanId}
        loanTask={currentDocument}
      />
      <DocReviewDialog
        open={documentFormStore.currentForm === TITLE_DOCUMENTS_REVIEW}
        onClose={closeCurrentForm}
        type={AIDocType.TITLE}
        loanId={loanId}
        loanTask={currentDocument}
      />
      <DocReviewDialog
        open={documentFormStore.currentForm === POST_CLOSING_REVIEW}
        onClose={closeCurrentForm}
        type={AIDocType.POST_CLOSING_REVIEW}
        loanId={loanId}
        loanTask={currentDocument}
      />
      <InsuranceApprovalDialog
        open={documentFormStore.currentForm === INSURANCE_APPROVAL}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      <InsuranceInformationAndReviewDialog
        open={documentFormStore.currentForm === INSURANCE_INFORMATION}
        onClose={closeCurrentForm}
        document={currentDocument}
        insuranceInformationStore={insuranceInformationStore}
        insuranceInformationReviewStore={insuranceInformationReviewStore}
        loanId={loanId}
      />
      <InsuranceInformationReviewDialog
        open={documentFormStore.currentForm === INSURANCE_INFORMATION_REVIEW}
        onClose={closeCurrentForm}
        document={currentDocument}
        store={insuranceInformationReviewStore}
        loanId={loanId}
      />
      <InsuranceDocumentDialog
        open={documentFormStore.currentForm === INSURANCE_DOCUMENT}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      <ExtensionDetailsDialog
        open={documentFormStore.currentForm === EXTENSION_DETAILS}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      <ExtensionAgreementDialog
        open={documentFormStore.currentForm === EXTENSION_AGREEMENT}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      <FundingTemplateDialog
        open={documentFormStore.currentForm === LOAN_FUNDING_TEMPLATE}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      <EstimateFundingTemplateDialog
        open={documentStore.openEstimateFundingTemplate}
        onClose={() => documentStore.setOpenEstimateFundingTemplate(false)}
        document={currentDocument}
      />
      <BorrowerClosingAgentDialog
        open={documentFormStore.currentForm === BORROWER_CLOSING_AGENT}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      <ScopeOfWorkV2LinkDialog
        open={documentFormStore.currentForm === SCOPE_OF_WORK_LINK_V2}
        onClose={closeCurrentForm}
        loanId={currentDocument?.loanId}
        propertyId={currentDocument?.collateralId}
        propertyAddress={currentDocument?.collateralAddress}
        store={documentFormStore.scopeOfWorkV2LinkStore}
        document={currentDocument}
      />
      <ScopeOfWorkV2FormDialog
        open={documentFormStore.currentForm === SCOPE_OF_WORK_FORM_V2}
        onClose={closeCurrentForm}
        store={documentFormStore.scopeOfWorkV2FormStore}
        taskId={currentDocument?.taskId}
      />
      <NoteAllongeDialog
        open={documentFormStore.currentForm === NOTE_ALLONGE}
        onClose={closeNoteAllongeAndAssignment}
        document={currentDocument}
        loan={loanStore.loanDetails}
        store={documentFormStore.documentSigningStore}
      />
      <AssignmentAndConveyanceDialog
        open={documentFormStore.currentForm === ASSIGNMENT_AND_CONVEYANCE}
        onClose={closeNoteAllongeAndAssignment}
        document={currentDocument}
        loan={loanStore.loanDetails}
        store={documentFormStore.documentSigningStore}
      />
      <PointOfContactDialog
        open={documentFormStore.currentForm === POINT_OF_CONTACT}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      <RateLockExtensionDialog
        open={documentFormStore.currentForm === RATE_LOCK_EXTENSION}
        onClose={closeCurrentForm}
        loan={loanStore.loanDetails}
        document={currentDocument}
      />
      <TitleCompanyBankInformationDialog
        open={documentFormStore.currentForm === TITLE_COMPANY_BANK_INFORMATION}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      {/* Not used anymore */}
      <PlaidCopyUrlModal
        isOpen={documentStore.openSharePlaidLinkModal}
        url={plaidShareBaseURL[0] + 'borrower-portal/profile'}
        handleClose={() => { documentStore.openSharePlaidLinkModal = false }}
        handleCopy={() => {
          navigator.clipboard.writeText(plaidShareBaseURL[0] + 'borrower-portal/profile');
          documentStore.openSharePlaidLinkModal = false
        }}
      />
      <ManualTasksItem
        open={documentFormStore.currentForm === MANUAL_TASK}
        onClose={closeCurrentForm}
        loanTaskId={currentDocument?.loanTaskId}
        document={currentDocument}
      />
      <ResolutionItem
        open={documentFormStore.currentForm === RESOLUTION_ITEM_FORM}
        onClose={closeCurrentForm}
        loanTaskId={currentDocument?.taskId}
        document={currentDocument}
      />
      {currentDocument && <CertificateOfGoodStandingDialog
        open={documentFormStore.currentForm === CERTIFICATE_OF_GOOD_STANDING}
        onClose={closeCurrentForm}
        document={currentDocument}
        expand={false} />}
      <TrackRecordResolutionItem
        open={documentFormStore.currentForm === TRACK_RECORD_RESOLUTION_ITEM_FORM}
        onClose={closeCurrentForm}
        loanTaskId={currentDocument?.taskId}
        document={currentDocument}
      />
      <DrawReportDialog
        open={documentFormStore.currentForm === DRAW_REPORT}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      <BorrowerBackgroundReviewDialog
        open={documentFormStore.currentForm === BORROWER_BACKGROUND_REVIEW}
        onClose={closeCurrentForm}
        loanId={loanId}
        document={currentDocument}
        loan={loanStore.loanDetails}
        taskId={currentDocument?.taskId}
      />
      <BorrowerBackgroundReviewDialog
        open={documentFormStore.currentForm === BACKGROUND_REPORT}
        onClose={closeCurrentForm}
        loanId={loanId}
        document={currentDocument}
        loan={loanStore.loanDetails}
        taskId={currentDocument?.taskId}
      />
      <BorrowerCreditReviewDialog
        open={documentFormStore.currentForm === BORROWER_CREDIT_REVIEW}
        onClose={closeCurrentForm}
        loanId={loanId}
        document={currentDocument}
        loan={loanStore.loanDetails}
        taskId={currentDocument?.taskId}
      />
      <BorrowerCreditReviewDialog
        open={documentFormStore.currentForm === TRIMERGE_CREDIT_REPORT}
        onClose={closeCurrentForm}
        loanId={loanId}
        document={currentDocument}
        loan={loanStore.loanDetails}
        taskId={currentDocument?.taskId}
      />
      {/* Both portals use the new v2 Track Record UI (TrackRecordLineItemDialog).
          It relies on the same shared stores, so logic is unchanged; only the
          visual design differs. Internal vs public behavior is handled inside
          the dialog via userStore.isNportalUser. */}
      <TrackRecordLineItemDialog
        open={documentFormStore.currentForm === TRACK_RECORD}
        onClose={closeCurrentForm}
        loanId={loanId}
        document={currentDocument}
        taskId={currentDocument?.taskId}
      />
      {/* TODO: double check if we can delete this */}
      {/* <CreditReport
        open={documentFormStore.currentForm === CREDIT_REPORT}
        onClose={closeCurrentForm}
        loanTaskId={currentDocument?.loanTaskId}
        document={currentDocument}
      />
      <CreditReport
        open={documentFormStore.currentForm === TRIMERGE_CREDIT_REPORT}
        onClose={closeCurrentForm}
        loanTaskId={currentDocument?.loanTaskId}
        document={currentDocument}
      /> */}
      <LoanClosingConditionModal
        open={isClosingConditionForm}
        onClose={closeCurrentForm}
        loanTaskId={currentDocument?.loanTaskId}
        taskName={currentDocument?.taskName}
      />
      <AppraisalOrderDetailsWithARRDialog
        open={documentFormStore.currentForm === APPRAISAL_DETAILS}
        onClose={closeCurrentForm}
        loan={loanStore.loanDetails}
        document={currentDocument}
      />
      <AppraisalDetailsDialogV2
        open={documentFormStore.currentForm === VALUATION_DETAILS}
        onClose={closeCurrentForm}
        loanId={Number(loanId)}
      />
      <AppraisalPaymentLinkModal
        open={documentFormStore.currentForm === VALUATION_PAYMENT}
        onClose={closeCurrentForm}
        store={appraisalDetailsV2Store}
        loanId={loanStore.loanDetails?.loanId}
        document={currentDocument}
      />
      <InternalValuationPaymentLinkForm
        open={documentFormStore.currentForm === INTERNAL_VALUATION}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      <AppraisalOrderDetailsDialog
        open={documentFormStore.currentForm === APPRAISAL_ORDER_DETAILS}
        onClose={closeCurrentForm}
        loanId={currentDocument?.loanId}
        collateralId={currentDocument?.collateralId}
        document={currentDocument}
      />
      <AppraisalReviewReportDialog
        open={documentFormStore.currentForm === APPRAISAL_REPORT_ARV}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      <AppraisalReviewReportDialog
        open={documentFormStore.currentForm === APPRAISAL_REPORT_AS_IS}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      <AppraisalReviewReportDialog
        open={documentFormStore.currentForm === APPRAISAL_REPORT_AS_IS_RENT}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      <AppraisalReviewReportDialog
        open={documentFormStore.currentForm === APPRAISAL_REPORT}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      {/* <BorrowerSetupDialog
        open={documentFormStore.currentForm === BORROWER_SETUP}
        onClose={closeCurrentForm}
        loanTaskId={currentDocument?.loanTaskId}
        taskName={currentDocument?.taskName}
        borrowerId={currentDocument?.borrowerId}
        loanType={loanStore.loanDetails?.loanType}
        loanId={currentDocument?.loanId}
      /> */}
      <BorrowerSetupToDoDialog
        open={documentFormStore.currentForm === BORROWER_SETUP}
        borrowerId={currentDocument?.borrowerId}
        loan={loanStore.loanDetails}
        handleClose={closeCurrentForm}
        refreshLoan={() => {
          loanStore.refreshLoanDetails();
          documentStore.loanTodosStore.fetchTodos(currentDocument?.loanId, LoanProcess.UNDERWRITING);
        }}
        document={currentDocument}
      />
      <PersonalAndCreditInformationDialog
        open={documentFormStore.currentForm === CREDIT_AUTHORIZATION}
        borrowerId={currentDocument?.borrowerId}
        loan={loanStore.loanDetails}
        handleClose={closeCurrentForm}
        refreshLoan={() => {
          loanStore.refreshLoanDetails();
          documentStore.loanTodosStore.fetchTodos(currentDocument?.loanId, LoanProcess.UNDERWRITING);
        }}
        document={currentDocument}
      />
      <IdAndFinancialInformationDialog
        open={documentFormStore.currentForm === BORROWER_IDENTITY_VERIFICATION}
        borrowerId={currentDocument?.borrowerId}
        loan={loanStore.loanDetails}
        handleClose={closeCurrentForm}
        refreshLoan={() => {
          loanStore.refreshLoanDetails();
          documentStore.loanTodosStore.fetchTodos(currentDocument?.loanId, LoanProcess.UNDERWRITING);
        }}
        document={currentDocument}
      />
      <AppraisalReviewReportDialog
        open={documentFormStore.currentForm === APPRAISAL_REVIEW_REPORT}
        onClose={closeCurrentForm}
        document={currentDocument}
      />

      <BankStatementDialog
        open={documentFormStore.currentForm && (documentFormStore.currentForm.includes(BANK_STATEMENT_PREFIX) || documentFormStore.currentForm.includes(BANK_STATEMENT_MONTH_PREFIX)) || documentFormStore.currentForm === BANK_STATEMENT_TASK_CODE}
        onClose={closeCurrentForm}
        loan={loanStore.loanDetails}
        document={currentDocument}
      />
      <TaxEscrowDialog
        open={documentFormStore.currentForm && documentFormStore.currentForm.includes(TAX_ESCROW)}
        onClose={closeCurrentForm}
        loan={loanStore.loanDetails}
        document={currentDocument}
        refreshLoan={() => {
          loanStore.refreshLoanDetails();
          documentStore.loanTodosStore.fetchTodos(currentDocument?.loanId, LoanProcess.UNDERWRITING);
        }}
      />
      <PostDisasterInspectionDialog
        open={documentFormStore.currentForm && documentFormStore.currentForm === POST_DISASTER_INSPECTION}
        onClose={closeCurrentForm}
        loan={loanStore.loanDetails}
        document={currentDocument}
      />
      <PersonalIdentificationDialog
        open={documentFormStore.currentForm && documentFormStore.currentForm === PERSONAL_IDENTIFICATION}
        onClose={closeCurrentForm}
        loan={loanStore.loanDetails}
        document={currentDocument}
      />
      <ConstructionFeasibilityToDoDialog
        open={documentFormStore.currentForm && documentFormStore.currentForm === CONSTRUCTION_FEASIBILITY_REVIEW}
        onClose={closeCurrentForm}
        loanId={loanId}
        document={currentDocument}
      />
      <InternalValuationReportDialog
        open={documentFormStore.currentForm && documentFormStore.currentForm === INTERNAL_VALUATION_REPORT}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      <PermitsDialog
        open={documentFormStore.currentForm && documentFormStore.currentForm === PERMITS}
        onClose={closeCurrentForm}
        loan={loanStore.loanDetails}
        document={currentDocument}
      />
      <DrawReviewReportDialog
        open={documentFormStore.currentForm === DRAW_REQUEST_FORM}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      <DrawInformationDialog
        open={documentFormStore.currentForm === DRAW_INFORMATION}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      <WireAuthorizationDialog
        open={documentFormStore.currentForm === WIRE_AUTHORIZATION}
        onClose={closeCurrentForm}
        loanId={currentDocument?.loanId}
        document={currentDocument}
      />
      <InspectionInvoiceDetailsDialog
        open={documentFormStore.currentForm === INSPECTION_INVOICE_DETAILS}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      <RevisedScopeOfWorkFormDialog
        open={documentFormStore.currentForm === REVISED_SOW_FORM}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      <TakePropertyPhotosDialog
        open={documentFormStore.currentForm === TAKE_PROPERTY_PHOTOS}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      <GenericPointOfContact
        open={documentFormStore.currentForm === SITE_INSPECTION_CM_REPORT}
        onClose={closeCurrentForm}
        document={currentDocument}
        type='SITE_INSPECTION'
      />
      <PayoffRequestDetailDialog
        open={documentFormStore.currentForm === PAYOFF_REQUEST_DETAILS}
        onClose={closeCurrentForm}
        loanId={loanStore.loanDetails?.loanId}
        isEditMode={true}
        document={currentDocument}
      />
      <ServicerProvidedDataDialog
        open={documentFormStore.currentForm === SERVICER_PROVIDED_DATA}
        onClose={closeCurrentForm}
        loan={loanStore.loanDetails}
        document={currentDocument}
      />
      <ServicerFees
        open={documentFormStore.currentForm === SERVICER_FEES}
        onClose={closeCurrentForm}
        loan={loanStore.loanDetails}
        document={currentDocument}
      />
      <InterestAllocationModal
        open={documentFormStore.currentForm === INTEREST_ALLOCATION}
        onClose={closeCurrentForm}
        loanId={loanStore.loanDetails?.loanId}
        document={currentDocument}
      />
      <ForeclosureInterestAllocationModal
        open={documentFormStore.currentForm === FORECLOSURE_INTEREST_ALLOCATION}
        onClose={closeCurrentForm}
        loanId={loanStore.loanDetails?.loanId}
        investor={loanStore.loanDetails?.investorName}
        document={currentDocument}
      />
      <PayoffCorrectionDialog
        open={documentFormStore.currentForm === PAYOFF_CORRECTION}
        onClose={closeCurrentForm}
        loanId={loanStore.loanDetails?.loanId}
        document={currentDocument}
      />
      <AttestationDialog
        open={documentFormStore.currentForm === PAYABLE_ATTESTATION || documentFormStore.currentForm === DQ_TAXES_ATTESTATION}
        type={documentFormStore.currentForm === PAYABLE_ATTESTATION ? 'Payable' : 'DQ Taxes'}
        onClose={closeCurrentForm}
        loanId={loanStore.loanDetails?.loanId}
        document={currentDocument}
      />
      <PayoffCalculatorDialog
        open={documentFormStore.currentForm === PAYOFF_CALCULATOR}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      <PayoffReconciliationReviewDialog
        open={documentFormStore.currentForm === PAYOFF_RECONCILIATION_REVIEW}
        onClose={closeCurrentForm}
        document={currentDocument}
        allowPayoffWaiverReview={globalStore.userFeatures.allowPayoffWaiverReview}
      />
      <RealValPropertyPhotosDialog
        open={documentFormStore.currentForm === TAKE_INTERNAL_VALUATION_PHOTOS}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      <FirstAppraisalValueConfirmationDialog
        open={documentFormStore.currentForm === CONFIRM_FIRST_APPRAISAL_VALUE}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      <SettlementDetailsItem
        open={documentFormStore.currentForm === SETTLEMENT_DETAILS}
        onClose={closeCurrentForm}
        document={currentDocument}
        loanId={loanId}
      />
      <TitleAgencyInfoItem
        open={documentFormStore.currentForm === TITLE_AGENCY}
        onClose={closeCurrentForm}
        showAlert={titleAgencyShowAlert}
        document={currentDocument}
      />
      <RealizedLossItem
        open={documentFormStore.currentForm === REALIZED_LOSS}
        onClose={closeCurrentForm}
        loanId={loanId}
        document={currentDocument}
      />
      <DelinquencyStatusItem
        open={documentFormStore.currentForm === DELINQUENCY_STATUS}
        onClose={closeCurrentForm}
        statusValues={DEFAULT_DELINQUENCY_STATUS_VALUES}
        loanId={loanId}
        document={currentDocument}
      />
      <PostMortemItem
        open={documentFormStore.currentForm === POST_MORTEM}
        onClose={closeCurrentForm}
        loanId={loanId}
        document={currentDocument}
      />
      <PropertyAppraisalsItem
        open={documentFormStore.currentForm === PROPERTY_APPRAISALS}
        onClose={closeCurrentForm}
        properties={loanStore?.loanDetails?.properties}
        document={currentDocument}
      />
      <DelinquencyBpoDashboardsItem
        open={documentFormStore.currentForm === PROPERTY_VALUATIONS}
        onClose={closeCurrentForm}
        loanId={loanId}
        document={currentDocument}
        isInternal
        isTamariskLender={false}
      />
      <ForeclosureStageListItem
        open={documentFormStore.currentForm === FORECLOSURE_STAGES}
        onClose={closeCurrentForm}
        loanId={loanId}
        document={currentDocument}
      />
      <ForeclosureAttorneyItem
        open={documentFormStore.currentForm === FORECLOSURE_ATTORNEY}
        onClose={closeCurrentForm}
        loanId={loanId}
        document={currentDocument}
      />
      <UnderwriteTheLoanDialog
        open={documentFormStore.currentForm === UNDERWRITE_THE_LOAN}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      <PrescreenDialog
        open={documentFormStore.currentForm === PRESCREEN}
        onClose={closeCurrentForm}
        loanId={loanStore.loanDetails?.loanId}
        taskId={currentDocument?.taskId}
      />
      <ValuationDisputeDialog
        open={documentFormStore.currentForm === CLIENT_VALUATION_DISPUTE || documentFormStore.currentForm === INTERNAL_VALUATION_ROV_FOR_APPRAISAL}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      <InsuranceDetailsItem
        open={documentFormStore.currentForm === INSURANCE_DETAILS}
        onClose={closeCurrentForm}
        loanId={loanId}
        document={currentDocument}
      />
      <AppraisalReassignmentLetterDialog
        open={documentFormStore.currentForm === APPRAISAL_REASSIGNMENT_LETTER}
        onClose={closeCurrentForm}
        loanId={loanStore.loanDetails?.loanId}
        propertyId={currentDocument?.collateralId}
      />
      {getCorrectAcceptLoanTermDialog()}
      <ResolutionModal
        open={documentFormStore.currentForm === RESOLUTION_FORM}
        handleClose={closeCurrentForm}
        document={currentDocument}
      />
      <DocReviewDialogV2
        open={documentFormStore.currentForm === DOC_REVIEW_FORM}
        handleClose={closeCurrentForm}
        taskId={currentDocument?.taskId}
      />
      <PurchaseContractReviewDialog
        open={documentFormStore.currentForm === PURCHASE_CONTRACT}
        handleClose={closeCurrentForm}
        taskId={currentDocument?.taskId}
      />
      <TitleCommitmentDialog
        open={documentFormStore.currentForm === TITLE_COMMITMENT}
        onClose={closeCurrentForm}
        taskId={currentDocument?.taskId}
      />
      <ResolutionModal
        open={documentFormStore.currentForm === LIEN_RESOLUTION_DOCUMENT}
        handleClose={closeCurrentForm}
        document={currentDocument}
        customContent={
          <LienResolutionContent
            document={currentDocument}
          />
        }
      />
      <BorrowerLiquidityDialog
        open={documentFormStore.currentForm === BORROWER_LIQUIDITY}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      <LeaseModal
        open={documentFormStore.currentForm === LEASE_AGREEMENT}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      <LeaseUnitModal
        open={!!documentFormStore.currentForm?.startsWith(LEASE_INFORMATION_UNIT_PREFIX)}
        onClose={closeCurrentForm}
        document={currentDocument}
      />
      <StateLicenseTaskDialog
        open={documentFormStore.currentForm === ORIGINATOR_STATE_LICENSE || documentFormStore.currentForm === COMPANY_STATE_LICENSE}
        onClose={closeCurrentForm}
        document={currentDocument}
        onSubmitted={() => {
          closeCurrentForm();
          documentStore.loanTodosStore.fetchTodosForMultipleLoanProcesses(currentDocument?.loanId, LoanProcess.TODOS, [LoanProcess.UNDERWRITING, LoanProcess.CLOSING], true);
        }}
      />
      {/* Add additional forms in here */}
    </>
  );
});

export default DocumentForms;
